import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PlatformService } from '../native/platform.service';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class NetworkStatusService {
  private readonly platform = inject(PlatformService);
  private readonly notifications = inject(NotificationService);
  private isOnline = navigator.onLine;
  private router: Router | null = null;

  constructor() {
    this.setupOnlineOfflineListeners();
  }

  /**
   * Initialize router reference (called from app component)
   */
  initialize(router: Router): void {
    this.router = router;

    // Check initial status
    if (!this.isOnline) {
      this.handleOffline();
    }
  }

  /**
   * Setup online/offline event listeners
   */
  private setupOnlineOfflineListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('Network connection restored');
      this.handleOnline();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('Network connection lost');
      this.handleOffline();
    });
  }

  /**
   * Handle when network comes back online
   */
  private handleOnline(): void {
    if (this.platform.isNative) {
      this.notifications.showSuccess('Back online. Syncing offline transactions…', 3000);
      return;
    }

    // If we're on network-error page, try to go back or reload
    if (window.location.pathname.includes('network-error')) {
      const returnUrl = sessionStorage.getItem('network_error_return_url');

      if (returnUrl) {
        // Clear the stored URL
        sessionStorage.removeItem('network_error_return_url');
        // Navigate to the stored URL
        window.location.href = returnUrl;
      } else if (window.history.length > 1) {
        window.history.back();
      } else {
        this.router?.navigate(['/dashboard']).catch(() => {
          window.location.href = '/dashboard';
        });
      }
    }
  }

  /**
   * Handle when network goes offline
   */
  private handleOffline(): void {
    // The native app keeps working offline against SQLite; only the web app is blocked.
    if (this.platform.isNative) {
      this.notifications.showWarning('You are offline. Entries and exits are saved on this device and will sync automatically.', 6000);
      return;
    }

    // Only redirect if router is initialized and not already on network-error page
    if (this.router && !window.location.pathname.includes('network-error')) {
      // Check if we're on a public route (login, signup)
      const publicRoutes = ['/login', '/signup'];
      const currentPath = window.location.pathname;

      if (!publicRoutes.some(route => currentPath.includes(route))) {
        // Store current URL before redirecting
        sessionStorage.setItem('network_error_return_url', window.location.href);

        this.router.navigate(['/network-error']).catch(() => {
          // Fallback if router navigation fails
          window.location.href = '/network-error';
        });
      }
    }
  }

  /**
   * Get current online status
   */
  getOnlineStatus(): boolean {
    return this.isOnline;
  }
}

